import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { SHOP } from "../data/content";
import useReveal from "../hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ShopProductPage — /shop/:slug
 * Demand-validation product page. No payment, no checkout — visitors join
 * the first-batch interest list. Images are the creator's original artwork,
 * displayed uncropped (object-contain) with no overlays.
 */
export default function ShopProductPage() {
    useReveal();
    const { slug } = useParams();
    const product = SHOP.products.find((p) => p.slug === slug);
    const [open, setOpen] = useState(false);

    if (!product) return <Navigate to="/shop" replace />;

    const journalOption = product.options.find((o) => o.id === "journal");
    const bundleOption = product.options.find(
        (o) => o.id === "journal_stickers",
    );

    return (
        <div className="pt-24" data-testid={`shop-product-${product.slug}`}>
            {/* PRODUCT HERO */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-14 lg:py-20">
                <Link
                    to="/shop"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] transition-colors"
                    data-testid="shop-back"
                >
                    <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Back to shop
                </Link>

                <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    {/* Cover image — the visual focal point */}
                    <div className="lg:col-span-6 ssh-reveal">
                        <div className="bg-[#F4F0FA] p-8 sm:p-12 lg:p-16 border border-[#E9E1F5]">
                            <img
                                src={product.coverImage}
                                alt={`${product.name} — front cover artwork`}
                                className="w-full h-auto object-contain shadow-[0_30px_80px_-24px_rgba(44,43,41,0.4)]"
                                data-testid="product-cover-image"
                            />
                        </div>
                    </div>

                    {/* Product intro */}
                    <div
                        className="lg:col-span-6 ssh-reveal"
                        style={{ transitionDelay: "0.1s" }}
                    >
                        <div className="flex flex-wrap items-center gap-3">
                            <p className="ssh-eyebrow">Journals</p>
                            <span
                                className="text-[10px] uppercase tracking-[0.18em] text-[#7C6AAE] border border-[#D9CFEC] bg-[#F4F0FA] px-3 py-1.5"
                                data-testid="product-status"
                            >
                                {product.status}
                            </span>
                        </div>
                        <h1 className="mt-6 font-serif font-light tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-[#2C2B29]">
                            {product.name}
                        </h1>
                        <p className="mt-6 font-serif text-xl lg:text-2xl italic leading-snug text-[#A68A6B]">
                            {product.tagline}
                        </p>
                        <p className="mt-5 text-base lg:text-lg leading-relaxed text-[#4A4947] max-w-xl">
                            {product.description}
                        </p>

                        <div className="mt-8 border-t border-[#EFEBE4] pt-6">
                            <p className="ssh-eyebrow text-[#75736F]">
                                Expected launch pricing
                            </p>
                            <ul className="mt-4 space-y-2.5 text-base text-[#2C2B29]">
                                <li className="flex justify-between max-w-sm">
                                    <span>{journalOption.label}</span>
                                    <span
                                        className="font-serif text-xl"
                                        data-testid="price-journal"
                                    >
                                        {journalOption.priceLabel}
                                    </span>
                                </li>
                                <li className="flex justify-between max-w-sm">
                                    <span>{bundleOption.label}</span>
                                    <span
                                        className="font-serif text-xl"
                                        data-testid="price-bundle"
                                    >
                                        {bundleOption.priceLabel}
                                    </span>
                                </li>
                            </ul>
                            <p className="mt-3 text-xs italic text-[#75736F]">
                                Expected prices — not yet available for
                                purchase.
                            </p>
                        </div>

                        <div className="mt-8">
                            <Button
                                onClick={() => setOpen(true)}
                                className="ssh-btn-primary rounded-none px-10 py-7 text-xs uppercase tracking-[0.24em]"
                                data-testid="join-first-batch-cta"
                            >
                                Join the First Batch
                            </Button>
                            <p className="mt-4 text-sm text-[#75736F] max-w-md leading-relaxed">
                                {product.launchNote}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* INSIDE THE JOURNAL */}
            <section className="bg-[#FAF8F5] border-t border-[#EFEBE4]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5 ssh-reveal">
                        <p className="ssh-eyebrow">Inside the journal</p>
                        <h2 className="mt-5 font-serif font-light tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-[#2C2B29]">
                            Dream. Reflect.{" "}
                            <em className="italic text-[#A68A6B]">Create.</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-7 ssh-reveal">
                        <p className="text-lg leading-relaxed text-[#4A4947]">
                            {product.description} Each page is an invitation —
                            to slow down, to listen inward, and to give your
                            hopes a shape you can return to.
                        </p>
                        <ul className="mt-8 grid grid-cols-2 gap-4">
                            {product.pillars.map((pillar) => (
                                <li
                                    key={pillar}
                                    className="flex items-center gap-3 text-sm uppercase tracking-[0.16em] text-[#2C2B29] border border-[#EFEBE4] bg-white px-4 py-4"
                                >
                                    <Sparkles
                                        className="h-4 w-4 text-[#A68A6B] flex-shrink-0"
                                        strokeWidth={1.25}
                                    />
                                    {pillar}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* JOURNAL + STICKER PACK */}
            <section className="bg-white border-t border-[#EFEBE4]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <div className="lg:col-span-6 ssh-reveal">
                        <div className="border border-[#EFEBE4] bg-[#FAF8F5] p-6 sm:p-10">
                            <img
                                src={product.stickerImage}
                                alt="Soul Sync Healing sticker sheets — illustrated stickers included in the Journal + Sticker Pack"
                                className="w-full h-auto object-contain shadow-[0_18px_50px_-20px_rgba(44,43,41,0.3)]"
                                loading="lazy"
                                data-testid="product-sticker-image"
                            />
                        </div>
                    </div>
                    <div
                        className="lg:col-span-6 ssh-reveal"
                        style={{ transitionDelay: "0.1s" }}
                    >
                        <p className="ssh-eyebrow">The bundle</p>
                        <h2 className="mt-5 font-serif font-light tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-[#2C2B29]">
                            Journal + Sticker Pack —{" "}
                            <em className="italic text-[#A68A6B]">
                                {bundleOption.priceLabel}
                            </em>
                        </h2>
                        <p className="mt-6 text-base lg:text-lg leading-relaxed text-[#4A4947]">
                            Pair your journal with a set of six A4 sheets of
                            hand-illustrated stickers — gentle words, little
                            joys, and small reminders to place through your
                            pages as you dream, reflect, and create.
                        </p>
                        <p className="mt-4 text-sm italic text-[#75736F]">
                            Expected launch price · not yet available for
                            purchase.
                        </p>
                        <div className="mt-8">
                            <Button
                                onClick={() => setOpen(true)}
                                variant="outline"
                                className="rounded-none border-[#2C2B29] bg-transparent text-[#2C2B29] hover:bg-[#2C2B29] hover:text-white px-9 py-7 text-xs uppercase tracking-[0.22em] transition-colors inline-flex items-center gap-3"
                                data-testid="join-first-batch-cta-bundle"
                            >
                                Join the First Batch
                                <ArrowRight
                                    className="h-4 w-4"
                                    strokeWidth={1.5}
                                />
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            <InterestDialog
                open={open}
                onOpenChange={setOpen}
                product={product}
            />
        </div>
    );
}

/* ---------- First-batch interest dialog ---------- */
function InterestDialog({ open, onOpenChange, product }) {
    const empty = { name: "", email: "", whatsapp: "" };
    const [form, setForm] = useState(empty);
    const [option, setOption] = useState("");
    const [consent, setConsent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e.target.value }));

    const close = (v) => {
        onOpenChange(v);
        if (!v) {
            // reset after the close animation settles
            setTimeout(() => {
                setForm(empty);
                setOption("");
                setConsent(false);
                setDone(false);
            }, 250);
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.whatsapp.trim()) {
            toast.error("Please share your name, email, and WhatsApp number.");
            return;
        }
        if (!option) {
            toast.error("Please choose the option that interests you.");
            return;
        }
        if (!consent) {
            toast.error("Please tick the consent box so we can contact you.");
            return;
        }
        try {
            setLoading(true);
            await axios.post(`${API}/journal-interest`, {
                name: form.name.trim(),
                email: form.email.trim(),
                whatsapp: form.whatsapp.trim(),
                option,
                consent,
            });
            setDone(true);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Something went wrong. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent
                className="bg-white rounded-none border-[#EFEBE4] sm:max-w-lg"
                data-testid="interest-dialog"
            >
                {done ? (
                    <div className="py-8 text-center" data-testid="interest-success">
                        <h3 className="font-serif font-light text-3xl lg:text-4xl text-[#2C2B29]">
                            You&apos;re on the list ✨
                        </h3>
                        <p className="mt-4 text-base leading-relaxed text-[#4A4947] max-w-sm mx-auto">
                            Thank you for your interest. I&apos;ll let you know
                            when the first-batch pre-orders open.
                        </p>
                        <Button
                            onClick={() => close(false)}
                            className="mt-8 ssh-btn-primary rounded-none px-9 py-6 text-xs uppercase tracking-[0.22em]"
                            data-testid="interest-success-close"
                        >
                            Back to the journal
                        </Button>
                    </div>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle className="font-serif font-light text-2xl lg:text-3xl text-[#2C2B29]">
                                Join the first batch
                            </DialogTitle>
                            <DialogDescription className="text-sm text-[#75736F] leading-relaxed">
                                No payment today — this simply tells me
                                you&apos;d love the {product.name}, so the first
                                print run is shaped by real interest.
                            </DialogDescription>
                        </DialogHeader>
                        <form
                            onSubmit={onSubmit}
                            className="mt-4 space-y-5"
                            data-testid="interest-form"
                        >
                            <div className="space-y-2">
                                <Label
                                    htmlFor="ji-name"
                                    className="ssh-eyebrow text-[#75736F]"
                                >
                                    Name
                                </Label>
                                <Input
                                    id="ji-name"
                                    value={form.name}
                                    onChange={onChange("name")}
                                    placeholder="Your full name"
                                    className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                                    data-testid="interest-input-name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label
                                    htmlFor="ji-email"
                                    className="ssh-eyebrow text-[#75736F]"
                                >
                                    Email
                                </Label>
                                <Input
                                    id="ji-email"
                                    type="email"
                                    value={form.email}
                                    onChange={onChange("email")}
                                    placeholder="you@example.com"
                                    className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                                    data-testid="interest-input-email"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label
                                    htmlFor="ji-whatsapp"
                                    className="ssh-eyebrow text-[#75736F]"
                                >
                                    WhatsApp number
                                </Label>
                                <Input
                                    id="ji-whatsapp"
                                    value={form.whatsapp}
                                    onChange={onChange("whatsapp")}
                                    placeholder="+91 …"
                                    className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                                    data-testid="interest-input-whatsapp"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="ssh-eyebrow text-[#75736F]">
                                    Which option interests you?
                                </Label>
                                <div className="grid grid-cols-1 gap-2">
                                    {product.options.map((o) => (
                                        <button
                                            type="button"
                                            key={o.id}
                                            onClick={() => setOption(o.id)}
                                            className={`flex items-center justify-between gap-3 border px-4 py-3.5 text-sm text-left transition-colors ${
                                                option === o.id
                                                    ? "border-[#A68A6B] bg-[#FAF8F5] text-[#2C2B29]"
                                                    : "border-[#EFEBE4] text-[#4A4947] hover:border-[#A68A6B]"
                                            }`}
                                            data-testid={`interest-option-${o.id}`}
                                        >
                                            <span>{o.label}</span>
                                            {o.priceLabel && (
                                                <span className="font-serif text-base text-[#2C2B29]">
                                                    {o.priceLabel}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <label
                                className="flex items-start gap-3 cursor-pointer"
                                data-testid="interest-consent-wrap"
                            >
                                <Checkbox
                                    checked={consent}
                                    onCheckedChange={(v) =>
                                        setConsent(v === true)
                                    }
                                    className="mt-0.5 rounded-none border-[#A68A6B] data-[state=checked]:bg-[#A68A6B]"
                                    data-testid="interest-consent"
                                />
                                <span className="text-sm leading-relaxed text-[#4A4947]">
                                    I agree to be contacted about the launch
                                    and pre-orders for this journal.
                                </span>
                            </label>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full ssh-btn-primary rounded-none py-6 text-xs uppercase tracking-[0.24em]"
                                data-testid="interest-submit"
                            >
                                {loading ? (
                                    <>
                                        <Loader2
                                            className="h-4 w-4 animate-spin mr-2"
                                            strokeWidth={1.75}
                                        />
                                        Joining…
                                    </>
                                ) : (
                                    "Join the First Batch"
                                )}
                            </Button>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
